import axios from 'axios';

// KYC API Base URL - use localhost:5000 in development, production URL otherwise
const KYC_BASE_URL = process.env.NODE_ENV === 'development' 
  // ? 'http://localhost:5000/api/v1/kyc'  // Commented out - uncomment when needed
  ? 'https://api.v1.indexx.ai/api/v1/kyc'  // Using production URL for now
  : 'https://api.v1.indexx.ai/api/v1/kyc';

console.log('🔗 KYC API Base URL:', KYC_BASE_URL);

// Create KYC API instance
const KYC_API = axios.create({
  baseURL: KYC_BASE_URL,
});

// Add request interceptor to include auth token
KYC_API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    
    // Validate token exists and is not null/undefined/empty
    if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      console.warn('⚠️ No valid access token found in localStorage');
      // Don't add Authorization header if token is invalid
    }
    
    // Don't override Content-Type for FormData - let axios handle it
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
KYC_API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.code === 'ECONNREFUSED') {
      error.message = 'Cannot connect to backend server. Please ensure it\'s running on localhost:5000';
    } else if (error.response?.status === 401) {
      const token = localStorage.getItem('access_token');
      console.error('❌ 401 Unauthorized - Token may be expired or invalid');
      console.error('Token exists:', !!token);
      console.error('Token value:', token ? `${token.substring(0, 20)}...` : 'null');
      
      // Clear invalid token
      if (token) {
        console.warn('⚠️ Clearing potentially invalid token');
        localStorage.removeItem('access_token');
      }
      
      error.message = 'Authentication required. Please log in again.';
      
      // Redirect to login or show login popup
      if (typeof window !== 'undefined') {
        // Check if we're on the KYC page and show login popup
        if (window.location.pathname.includes('verify-with-indexx')) {
          // The component should handle showing login popup
          console.warn('⚠️ User needs to log in again');
        }
      }
    } else if (error.response?.status === 404) {
      error.message = 'KYC endpoints not found. Please check backend configuration.';
    } else if (error.response?.status >= 500) {
      error.message = 'Backend server error. Please try again later.';
    }
    return Promise.reject(error);
  }
);

// Types
export interface PersonalInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string; // ISO date string
  address: string;
  city: string;
  country: string;
  postalCode: string;
  nationality: string;
}

export interface KycApplication {
  id: string;
  levelRequested: number;
  personalInfo?: PersonalInfo;
  status?: 'draft' | 'pending' | 'under_review' | 'approved' | 'rejected' | 'need_more_info';
  rejectionReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface KycStatus {
  status: 'draft' | 'pending' | 'under_review' | 'approved' | 'rejected' | 'need_more_info';
  level?: number;
  applicationId?: string;
  rejectionReason?: string;
  documents?: {
    type: string;
    status: string;
  }[];
}

export interface PresignedUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface DocumentUpload {
  type: 'ID_FRONT' | 'ID_BACK' | 'PASSPORT' | 'SELFIE' | 'PROOF_OF_ADDRESS';
  s3Key: string;
  mimeType: string;
  fileSize: number;
}

export interface ApplicationDocument {
  _id: string;
  type: string;
  s3Key: string;
  mimeType: string;
  fileSize: number;
  side?: 'front' | 'back';
  createdAt: string;
  url?: string; // Presigned URL for viewing the document
}

/**
 * Create a new KYC application
 */
export const createKycApplication = async (
  levelRequested: number,
  personalInfo: PersonalInfo
): Promise<{ data: KycApplication }> => {
  try {
    const response = await KYC_API.post('/applications', {
      levelRequested,
      personalInfo,
    });
    return response;
  } catch (error: any) {
    console.error('Failed to create KYC application:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update an existing KYC application
 */
export const updateKycApplication = async (
  id: string,
  personalInfo: Partial<PersonalInfo>
): Promise<{ data: KycApplication }> => {
  try {
    const response = await KYC_API.patch(`/applications/${id}`, {
      personalInfo,
    });
    return response;
  } catch (error: any) {
    console.error('Failed to update KYC application:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get current KYC status
 */
export const getKycStatus = async (): Promise<{ data: KycStatus }> => {
  try {
    const response = await KYC_API.get('/status');
    return response;
  } catch (error: any) {
    // Don't log 404 as error - it's normal when no application exists
    if (error.response?.status === 404) {
      console.log('No KYC application found (404) - this is normal for new users');
    } else {
      console.error('Failed to get KYC status:', error);
    }
    throw error.response?.data || error;
  }
};

/**
 * Get presigned URL for document upload
 */
export const getPresignedUploadUrl = async (
  applicationId: string,
  type: 'ID_FRONT' | 'ID_BACK' | 'PASSPORT' | 'SELFIE' | 'PROOF_OF_ADDRESS',
  mimeType: string
): Promise<{ data: PresignedUrlResponse }> => {
  try {
    const response = await KYC_API.post(`/applications/${applicationId}/documents/presign`, {
      type,
      mimeType,
    });
    return response;
  } catch (error: any) {
    console.error('Failed to get presigned URL:', error);
    throw error.response?.data || error;
  }
};

/**
 * Confirm document upload after uploading to S3
 */
export const confirmDocumentUpload = async (
  applicationId: string,
  document: DocumentUpload
): Promise<{ data: { success: boolean } }> => {
  try {
    const response = await KYC_API.post(`/applications/${applicationId}/documents`, document);
    return response;
  } catch (error: any) {
    console.error('Failed to confirm document upload:', error);
    throw error.response?.data || error;
  }
};

/**
 * Upload document directly to backend (backend handles S3 upload and creates application if needed)
 */
export const uploadDocumentDirect = async (
  type: 'ID_FRONT' | 'ID_BACK' | 'PASSPORT' | 'SELFIE' | 'PROOF_OF_ADDRESS',
  file: File,
  selectedDocumentType?: 'PASSPORT' | 'NATIONAL_ID' | 'DRIVING_LICENSE' | null,
  side?: 'front' | 'back',
  countryCode?: string
): Promise<{ 
  status: number;
  data: { 
    applicationId?: string;
    document?: { 
      _id: string;
      type: string;
      s3Key: string;
      mimeType: string;
      fileSize: number;
      createdAt: Date;
    };
    message?: string;
    s3Key?: string;
  } 
}> => {
  try {
    // Convert File to base64 string (data URL format)
    const base64Image = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Result is in data URL format: "data:image/jpeg;base64,..."
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

    // Map document type to backend format
    let documentType: string = type;
    let documentSide: string | undefined = side;

    // If we have selected document type, map it properly
    if (selectedDocumentType) {
      if (selectedDocumentType === 'PASSPORT') {
        if (type === 'PASSPORT') {
          documentType = 'passport';
          documentSide = 'front';
        } else if (type === 'ID_BACK') {
          documentType = 'passport';
          documentSide = 'back';
        }
      } else if (selectedDocumentType === 'NATIONAL_ID') {
        if (type === 'ID_FRONT') {
          documentType = 'national_id';
          documentSide = 'front';
        } else if (type === 'ID_BACK') {
          documentType = 'national_id';
          documentSide = 'back';
        }
      } else if (selectedDocumentType === 'DRIVING_LICENSE') {
        if (type === 'ID_FRONT') {
          documentType = 'driving_licence';
          documentSide = 'front';
        } else if (type === 'ID_BACK') {
          documentType = 'driving_licence';
          documentSide = 'back';
        }
      }
    } else {
      // Fallback: map based on type directly
      if (type === 'PASSPORT') {
        documentType = 'passport';
        documentSide = 'front';
      } else if (type === 'ID_FRONT') {
        documentType = 'ID_FRONT'; // Backend will handle mapping
      } else if (type === 'ID_BACK') {
        documentType = 'ID_BACK'; // Backend will handle mapping
      } else if (type === 'SELFIE') {
        documentType = 'SELFIE';
      } else if (type === 'PROOF_OF_ADDRESS') {
        documentType = 'PROOF_OF_ADDRESS';
      }
    }

    // Backend expects JSON body with base64 image string
    const requestBody: any = {
      image: base64Image, // Base64 data URL string
      documentType: documentType,
      mimeType: file.type || 'image/jpeg',
      levelRequested: 1,
    };

    // Add side field if applicable
    if (documentSide) {
      requestBody.side = documentSide;
    }

    if (
      countryCode &&
      documentType &&
      (documentType === 'national_id' || documentType === 'driving_licence')
    ) {
      requestBody.country = countryCode;
    }

    console.log('Uploading document:', {
      documentType: documentType,
      side: documentSide,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      base64Length: base64Image.length
    });

    // Backend route is /documents/upload (not /applications/:id/documents/upload)
    // Backend creates application automatically if it doesn't exist
    const response = await KYC_API.post('/documents/upload', requestBody, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return response;
  } catch (error: any) {
    console.error('Failed to upload document:', error);
    console.error('Error response:', error.response?.data);
    throw error.response?.data || error;
  }
};

/**
 * Get application documents
 */
export const getApplicationDocuments = async (
  applicationId: string
): Promise<{ 
  status: number;
  data: {
    documents: ApplicationDocument[];
  }
}> => {
  try {
    const response = await KYC_API.get(`/applications/${applicationId}/documents`);
    return response;
  } catch (error: any) {
    console.error('Failed to get application documents:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete a KYC application document
 */
export const deleteKycDocument = async (
  applicationId: string,
  documentId: string
): Promise<{ status: number; data: { message: string } }> => {
  try {
    const response = await KYC_API.delete(`/applications/${applicationId}/documents/${documentId}`);
    return response;
  } catch (error: any) {
    console.warn('Primary KYC delete document endpoint failed, attempting fallback', error);
    if (error.response?.status === 404) {
      try {
        const fallback = await KYC_API.delete(`/documents/${documentId}`);
        return fallback;
      } catch (fallbackError: any) {
        console.error('Fallback delete for KYC document also failed:', fallbackError);
        throw fallbackError.response?.data || fallbackError;
      }
    }
    console.error('Failed to delete KYC document:', error);
    throw error.response?.data || error;
  }
};

export interface DocumentSelectionPayload {
  country?: string;
  documentType?: string;
  documentCategory?: string;
}

/**
 * Save selected document type and country
 */
export const saveDocumentSelection = async (
  applicationId: string,
  payload: DocumentSelectionPayload
): Promise<{ data: any }> => {
  try {
    const response = await KYC_API.put(`/applications/${applicationId}/selection`, payload);
    return response;
  } catch (error: any) {
    console.error('Failed to save document selection:', error);
    throw error.response?.data || error;
  }
};

/**
 * Submit KYC application for review
 */
export const submitKycApplication = async (
  id: string
): Promise<{ data: KycApplication }> => {
  try {
    const response = await KYC_API.post(`/applications/${id}/submit`);
    return response;
  } catch (error: any) {
    console.error('Failed to submit KYC application:', error);
    throw error.response?.data || error;
  }
};
